import { Fragment, useMemo, useState } from 'react';
import type { HeatmapRow } from '../../types';
import { DropdownToggle } from '../shared/DropdownToggle';
import { formatSnapshotTimestamp } from '../../utils/formatTimestamp';
import styles from './EngagementHeatmap.module.css';

type Mode = 'total' | 'meetings' | 'emails';

const COLORS: Record<Mode, string[]> = {
  // Greens (matching the Staffing tab activity scale).
  total: ['#A5D6A7', '#66BB6A', '#43A047', '#2E7D32', '#1B5E20'],
  meetings: ['#A5D6A7', '#66BB6A', '#43A047', '#2E7D32', '#1B5E20'],
  emails: ['#90CAF9', '#42A5F5', '#1E88E5', '#1565C0', '#0D47A1'],
};

function valueFor(w: { emails: number; meetings: number }, mode: Mode): number {
  if (mode === 'meetings') return w.meetings;
  if (mode === 'emails') return w.emails;
  return w.meetings + w.emails;
}

function getColor(value: number, max: number, mode: Mode): string {
  if (value === 0) return '';
  const palette = COLORS[mode];
  const ratio = Math.min(value / Math.max(max, 1), 1);
  const idx = Math.min(Math.floor(ratio * palette.length), palette.length - 1);
  return palette[idx];
}

interface Props {
  data: HeatmapRow[];
  weekLabels: string[];
  snapshotTimestamp?: string | null;
}

type SortMode = 'alpha' | 'recent';
type GroupKey = 'portfolio' | 'pipeline';

export function EngagementHeatmap({ data, weekLabels, snapshotTimestamp }: Props) {
  const [mode, setMode] = useState<Mode>('total');
  const [sortMode, setSortMode] = useState<SortMode>('recent');
  const [collapsed, setCollapsed] = useState<Set<GroupKey>>(new Set());

  const toggleGroup = (k: GroupKey) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const maxValue = useMemo(() => {
    let max = 0;
    for (const row of data) {
      for (const w of row.weeks) {
        const v = valueFor(w, mode);
        if (v > max) max = v;
      }
    }
    return max;
  }, [data, mode]);

  // Sort rows within a group. Alpha = company_name ascending.
  // Recent = latest-week activity (by current mode) descending, tiebreak alpha.
  const sortRows = useMemo(() => {
    return (rows: HeatmapRow[]): HeatmapRow[] => {
      const copy = [...rows];
      if (sortMode === 'alpha') {
        copy.sort((a, b) => a.company_name.localeCompare(b.company_name));
      } else {
        const latestVal = (r: HeatmapRow): number => {
          const last = r.weeks[r.weeks.length - 1];
          return last ? valueFor(last, mode) : 0;
        };
        copy.sort((a, b) => {
          const diff = latestVal(b) - latestVal(a);
          if (diff !== 0) return diff;
          return a.company_name.localeCompare(b.company_name);
        });
      }
      return copy;
    };
  }, [mode, sortMode]);

  const portfolio = sortRows(data.filter((r) => r.group === 'portfolio'));
  const pipeline = sortRows(data.filter((r) => r.group === 'pipeline'));

  // Weekly sums for a group, keeping meetings/emails split so the subtotal
  // row can render "M/E" text in Total mode just like individual rows.
  const groupWeeklySums = (rows: HeatmapRow[]): { meetings: number; emails: number }[] => {
    const n = weekLabels.length;
    const out = Array.from({ length: n }, () => ({ meetings: 0, emails: 0 }));
    for (const r of rows) {
      for (let i = 0; i < Math.min(r.weeks.length, n); i++) {
        out[i].meetings += r.weeks[i].meetings;
        out[i].emails += r.weeks[i].emails;
      }
    }
    return out;
  };

  if (data.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.headerBar}>
          <div className={styles.headerLeft}>
            <span className={styles.headerLabel}>Engagement Heatmap</span>
          </div>
        </div>
        <div className={styles.empty}>
          No activity data yet. Engagement is computed by the native activity rollup
          (Admin &rarr; Schedules &rarr; <code>dash-activity-rollup</code>).
        </div>
      </div>
    );
  }

  const colCount = 1 + weekLabels.length;

  return (
    <div className={styles.card}>
      <div className={styles.headerBar}>
        <div className={styles.headerLeft}>
          <span className={styles.headerLabel}>Engagement Heatmap</span>
          <span className={styles.headerSub}>{weekLabels.length} weeks</span>
          {snapshotTimestamp && (
            <span className={styles.headerSub}>
              · as of {formatSnapshotTimestamp(snapshotTimestamp)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <DropdownToggle<SortMode>
            ariaLabel="Sort"
            value={sortMode}
            onChange={setSortMode}
            options={[
              { value: 'recent', label: 'Latest Week' },
              { value: 'alpha', label: 'A\u2013Z' },
            ]}
          />
          <DropdownToggle<Mode>
            ariaLabel="Activity metric"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'total', label: 'Total' },
              { value: 'meetings', label: 'Meetings' },
              { value: 'emails', label: 'Emails' },
            ]}
          />
        </div>
      </div>
      <div className={styles.scrollContainer}>
        <table className={styles.grid}>
          <thead>
            <tr>
              <th className={styles.cornerCell} />
              {weekLabels.map((label) => (
                <th key={label} className={styles.weekHeader}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {([
              { key: 'portfolio' as GroupKey, label: 'Portfolio', rows: portfolio },
              { key: 'pipeline' as GroupKey, label: 'Pipeline', rows: pipeline },
            ]).map((g) => {
              if (g.rows.length === 0) return null;
              const isCollapsed = collapsed.has(g.key);
              const subtotals = groupWeeklySums(g.rows);
              const subtotalValues = subtotals.map((s) => valueFor(s, mode));
              const groupMax = Math.max(1, ...subtotalValues);
              return (
                <Fragment key={g.key}>
                  <tr
                    className={styles.groupRow}
                    onClick={() => toggleGroup(g.key)}
                  >
                    <td colSpan={colCount} className={styles.groupCell}>
                      <span className={styles.groupChevron}>{isCollapsed ? '\u25B8' : '\u25BE'}</span>
                      {g.label} ({g.rows.length})
                    </td>
                  </tr>
                  {!isCollapsed && g.rows.map((row) => (
                    <HeatmapDataRow
                      // `crm_list_entry.id` — v1 keyed on `organization_id`, an Affinity id that is
                      // 0 for every row of the native payload (so every row shared one key).
                      key={row.entry_id}
                      row={row}
                      mode={mode}
                      maxValue={maxValue}
                    />
                  ))}
                  <tr className={styles.subtotalRow}>
                    <td className={`${styles.nameCell} ${styles.subtotalLabel}`}>
                      {g.label} subtotal
                    </td>
                    {subtotals.map((s, i) => {
                      const value = valueFor(s, mode);
                      const bg = value > 0 ? getColor(value, groupMax, mode) : '';
                      const text = value === 0
                        ? ''
                        : mode === 'total'
                          ? `${s.meetings}/${s.emails}`
                          : String(value);
                      const tip = `${g.label} subtotal: ${s.meetings} meetings, ${s.emails} emails`;
                      return (
                        <td key={i} className={styles.heatCell}>
                          <span
                            className={`${styles.heatDot} ${value === 0 ? styles.heatEmpty : ''}`}
                            style={bg ? { background: bg } : undefined}
                            title={tip}
                          >
                            {text}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={styles.legend}>
        <span>Less</span>
        {COLORS[mode].map((c, i) => (
          <div key={i} className={styles.legendBlock} style={{ background: c }} />
        ))}
        <span>More</span>
      </div>
    </div>
  );
}

function HeatmapDataRow({
  row,
  mode,
  maxValue,
}: {
  row: HeatmapRow;
  mode: Mode;
  maxValue: number;
}) {
  return (
    <tr className={styles.dataRow}>
      <td className={styles.nameCell}>
        <a
          href={`https://ember.affinity.co/opportunities/${row.affinity_opportunity_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.dealLink}
        >
          {row.company_name}
        </a>
      </td>
      {row.weeks.map((w) => {
        const value = valueFor(w, mode);
        const bg = value > 0 ? getColor(value, maxValue, mode) : '';
        const text = value === 0
          ? ''
          : mode === 'total'
            ? `${w.meetings}/${w.emails}`
            : String(value);
        const tip = `${w.meetings} meetings, ${w.emails} emails`;
        return (
          <td key={w.week_start} className={styles.heatCell}>
            <span
              className={`${styles.heatDot} ${value === 0 ? styles.heatEmpty : ''}`}
              style={bg ? { background: bg } : undefined}
              title={tip}
            >
              {text}
            </span>
          </td>
        );
      })}
    </tr>
  );
}
